import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Building2, Users, Euro, Store, FileText,
  CheckCircle2, XCircle, Shield,
  Lock, MessageSquare, Edit
} from 'lucide-react';
import { useAlert } from '../../components/AlertProvider';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import adminClientMembersService from '../../services/adminClientMembersService';
import { ClientMember } from '../../types/client';

// Import extracted components and types
import {
  Client, SalesPoint, ClientPricing, PurchaseOrder, BuyerNote, TabType, ClientReminder
} from './components/ClientDetail/types';
import { getStatusStyle, getStatusLabel } from './components/ClientDetail/utils';
import { CompanyTab } from './components/ClientDetail/CompanyTab';
import { SalesPointsTab } from './components/ClientDetail/SalesPointsTab';
import { ContactsTab } from './components/ClientDetail/ContactsTab';
import { AdminTab } from './components/ClientDetail/AdminTab';
import { PricingTab } from './components/ClientDetail/PricingTab';
import { OrdersTab } from './components/ClientDetail/OrdersTab';
import { NotesTab } from './components/ClientDetail/NotesTab';
import { ClientEditModal } from './components/ClientDetail/ClientEditModal';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showConfirm } = useConfirm();
  const { user } = useAuth();
  const { showError, showSuccess } = useAlert();

  const [activeTab, setActiveTab] = useState<TabType>('company');
  const [client, setClient] = useState<Client | null>(null);
  const [contacts, setContacts] = useState<ClientMember[]>([]);
  const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
  const [pricing, setPricing] = useState<ClientPricing[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [notes, setNotes] = useState<BuyerNote[]>([]);
  const [reminders, setReminders] = useState<ClientReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderModalOpen, setOrderModalOpen] = useState<boolean>(false);
  const [centralPurchasingName, setCentralPurchasingName] = useState<string | null>(null);

  // Notes state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      loadClientData();
    }
  }, [id]);

  const loadClientData = async () => {
    setLoading(true);
    try {
      const clientRes = await api.get(`/clients/${id}`);
      const clientData = clientRes.data;
      setClient(clientData);

      if (clientData.central_purchasing_id) {
        try {
          const centralRes = await api.get(`/clients/${clientData.central_purchasing_id}`);
          console.log('Central Client Data:', centralRes.data);
          const centralName = centralRes.data.company_name || centralRes.data.name || 'Nom inconnu';
          setCentralPurchasingName(centralName);
        } catch (err) {
          console.error("Failed to load central purchasing name", err);
          showError("Impossible de charger le nom de la centrale d'achat");
          setCentralPurchasingName(null);
        }
      } else {
        setCentralPurchasingName(null);
      }
    } catch (error) {
      showError('Erreur lors du chargement du client');
      navigate('/achat/clients');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const contactsData = await adminClientMembersService.list(Number(id));
      setContacts(contactsData);
    } catch (error) {
      showError('Erreur lors du chargement des contacts');
    }
  };

  const loadSalesPoints = async () => {
    try {
      const res = await api.get(`/clients/${id}/sales-points`);
      setSalesPoints(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      showError('Erreur lors du chargement des points de vente');
    }
  };

  const loadPricing = async () => {
    try {
      // Determine which BPU to load
      // If client is using central purchasing BPU, load that one
      // Otherwise load the client's own BPU
      const targetClientId = (client?.use_central_purchasing_bpu && client?.central_purchasing_id)
        ? client.central_purchasing_id
        : id;

      const res = await api.get(`/client-pricing?client_id=${targetClientId}`);
      const list = Array.isArray(res.data) ? (res.data as ClientPricing[]) : [];
      const hasPricingItems = list.some((bpu) => Array.isArray(bpu.pricing_items));
      if (hasPricingItems) {
        setPricing(list);
        return;
      }

      const detailed = await Promise.all(
        list.map(async (bpu) => {
          const detailRes = await api.get(`/client-pricing/${bpu.id}`);
          return detailRes.data as ClientPricing;
        })
      );

      const consumableIds = Array.from(
        new Set(
          detailed
            .flatMap((bpu) => (Array.isArray(bpu.pricing_items) ? bpu.pricing_items : []))
            .map((item) => item.consumable_id)
        )
      );

      const consumables = await Promise.all(
        consumableIds.map(async (consumableId) => {
          try {
            const consumableRes = await api.get(`/consumables/${consumableId}`);
            return consumableRes.data;
          } catch {
            return null;
          }
        })
      );

      const consumableById = new Map<number, any>();
      consumables.forEach((c) => {
        if (c) {
          consumableById.set(c.id, c);
        }
      });

      const enriched: ClientPricing[] = detailed.map((bpu) => ({
        ...bpu,
        pricing_items: (bpu.pricing_items || []).map((item) => ({
          ...item,
          consumable: consumableById.get(item.consumable_id) ?? null,
        })),
      }));

      setPricing(enriched);
    } catch (error) {
      showError('Erreur lors du chargement des tarifs');
    }
  };

  const loadOrders = async () => {
    try {
      const res = await api.get(`/purchase-orders?client_id=${id}`);
      const list = Array.isArray(res.data) ? (res.data as PurchaseOrder[]) : [];
      const clientId = Number(id);
      const filtered = list.filter((order) =>
        typeof order.client_id === 'number' ? order.client_id === clientId : true
      );
      setOrders(filtered);
    } catch (error) {
      showError('Erreur lors du chargement des commandes');
    }
  };

  const loadNotes = async () => {
    try {
      const res = await api.get(`/clients/${id}/notes`);
      setNotes(res.data || []);
    } catch (error) {
      showError('Erreur lors du chargement des notes');
    }
  };

  const loadReminders = async () => {
    try {
      const res = await api.get(`/clients/${id}/reminders`);
      setReminders(res.data || []);
    } catch (error) {
      showError('Erreur lors du chargement des rappels');
    }
  };

  const handleAddTextNote = async () => {
    if (!newNoteContent.trim() || !id) return;
    try {
      await api.post(`/clients/${id}/notes`, {
        client_id: Number(id),
        note_type: 'text',
        content: newNoteContent,
      });
      setNewNoteContent('');
      await loadNotes();
      showSuccess('Note ajoutée');
    } catch (error) {
      showError('Erreur lors de l\'ajout de la note');
    }
  };

  const startRecording = async () => {
    try {
      // Check if browser supports mediaDevices
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showError('Votre navigateur ne supporte pas l\'enregistrement audio');
        return;
      }

      // Check if we're on HTTPS (required for microphone access on mobile)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        showError('L\'accès au microphone nécessite une connexion sécurisée (HTTPS)');
        return;
      }

      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        showError('L\'enregistrement audio n\'est pas supporté sur votre appareil');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        }
      });

      // Check if MediaRecorder can handle the stream
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        } else {
          showError('Format audio non supporté sur votre appareil');
          stream.getTracks().forEach(track => track.stop());
          return;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await handleVoiceNote(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        showError('Erreur lors de l\'enregistrement');
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error: any) {
      console.error('Microphone access error:', error);

      if (error.name === 'NotAllowedError') {
        showError('Permission refusée. Veuillez autoriser l\'accès au microphone dans les paramètres de votre navigateur');
      } else if (error.name === 'NotFoundError') {
        showError('Aucun microphone détecté sur votre appareil');
      } else if (error.name === 'NotReadableError') {
        showError('Microphone déjà utilisé par une autre application');
      } else if (error.name === 'OverconstrainedError') {
        showError('Paramètres audio non compatibles avec votre appareil');
      } else if (error.name === 'NotSupportedError') {
        showError('L\'enregistrement audio n\'est pas supporté sur votre navigateur');
      } else if (error.name === 'AbortError') {
        showError('Accès au microphone interrompu');
      } else {
        showError(`Impossible d'accéder au microphone: ${error.message || 'Erreur inconnue'}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);

      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  const handleVoiceNote = async (audioBlob: Blob) => {
    if (!id) return;
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice_note.webm');
      formData.append('client_id', id);

      const response = await api.post(`/clients/${id}/notes/voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Insérer la transcription dans le champ texte si disponible
      if (response.data?.transcription) {
        setNewNoteContent(response.data.transcription);
      }

      await loadNotes();
      showSuccess('Note vocale ajoutée et transcrite');
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    const confirmed = await showConfirm({
      title: 'Supprimer la note',
      message: 'Êtes-vous sûr de vouloir supprimer cette note ?',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/clients/${id}/notes/${noteId}`);
      showSuccess('Note supprimée');
      await loadNotes();
    } catch (error) {
      showError('Erreur lors de la suppression de la note');
    }
  };

  const handleUpdateNote = async (noteId: number, content: string) => {
    try {
      await api.patch(`/clients/${id}/notes/${noteId}`, { content });
      showSuccess('Note mise à jour');
      await loadNotes();
    } catch (error) {
      showError('Erreur lors de la mise à jour de la note');
    }
  };

  // Reminder functions
  const handleCreateReminder = async (reminderData: { content: string; reminder_datetime?: string; visible_to_user_ids?: number[]; timezone_offset?: number; local_time_str?: string }) => {
    if (!id) return;
    try {
      await api.post(`/clients/${id}/reminders`, reminderData);
      showSuccess('Rappel créé');
      await loadReminders();
    } catch (error) {
      showError('Erreur lors de la création du rappel');
    }
  };

  const handleUpdateReminder = async (reminderId: number, reminderData: { content?: string; reminder_datetime?: string; status?: string }) => {
    try {
      await api.put(`/clients/${id}/reminders/${reminderId}`, reminderData);
      showSuccess('Rappel mis à jour');
      await loadReminders();
    } catch (error) {
      showError('Erreur lors de la mise à jour du rappel');
    }
  };

  const handleDeleteReminder = async (reminderId: number) => {
    const confirmed = await showConfirm({
      title: 'Supprimer le rappel',
      message: 'Êtes-vous sûr de vouloir supprimer ce rappel ?',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/clients/${id}/reminders/${reminderId}`);
      showSuccess('Rappel supprimé');
      await loadReminders();
    } catch (error) {
      showError('Erreur lors de la suppression du rappel');
    }
  };

  const handleToggleReminderComplete = async (reminderId: number, isCompleted: boolean) => {
    try {
      await handleUpdateReminder(reminderId, {
        status: isCompleted ? 'completed' : 'pending'
      });
    } catch (error) {
      showError('Erreur lors de la mise à jour du statut du rappel');
    }
  };

  const handleSnoozeReminder = async (reminderId: number, newDatetime: string) => {
    try {
      await api.post(`/clients/${id}/reminders/${reminderId}/snooze`, {
        new_reminder_datetime: newDatetime,
      });
      showSuccess('Rappel reporté');
      await loadReminders();
    } catch (error) {
      showError('Erreur lors du report du rappel');
    }
  };

  const tabs = useMemo(() => [
    { id: 'company' as TabType, label: 'Société', icon: Building2 },
    { id: 'sales-points' as TabType, label: 'Points de vente', icon: Store },
    { id: 'contacts' as TabType, label: 'Contacts', icon: Users },
    ...(user?.role === 'super_admin' || user?.role === 'admin' ? [{ id: 'admin' as TabType, label: 'Apporteur', icon: Shield }] : []),
    { id: 'pricing' as TabType, label: 'BPU', icon: Euro },
    { id: 'orders' as TabType, label: 'Commandes', icon: FileText },
    { id: 'notes' as TabType, label: 'Notes & Rappels', icon: MessageSquare },
  ], [user?.role]);

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tabId);
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  useEffect(() => {
    if (!id) return;

    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab') as TabType;

    if (tabParam && tabs.some(t => t.id === tabParam)) {
      if (tabParam !== activeTab) {
        setActiveTab(tabParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search, tabs]);

  useEffect(() => {
    if (!id) return;
    switch (activeTab) {
      case 'contacts':
        loadContacts();
        break;
      case 'sales-points':
        loadSalesPoints();
        break;
      case 'pricing':
        if (client) loadPricing();
        break;
      case 'orders':
        loadOrders();
        if (client) loadPricing();
        break;
      case 'notes':
        loadNotes();
        loadReminders();
        break;
      default:
        break;
    }
  }, [activeTab, id, client, client?.use_central_purchasing_bpu, client?.central_purchasing_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                onClick={() => navigate('/achat/clients')}
                className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors shrink-0"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-slate-400" />
              </button>
              <h1 className="text-base sm:text-2xl font-bold text-gray-900 dark:text-slate-100 truncate">{client.company_name}</h1>
              <span className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium shrink-0 ${getStatusStyle(client.status)}`}>
                {client.status === 'actif' ? <CheckCircle2 className="w-3 h-3 mr-0.5 sm:mr-1" /> : (client.status === 'bloque' ? <Shield className="w-3 h-3 mr-0.5 sm:mr-1" /> : <XCircle className="w-3 h-3 mr-0.5 sm:mr-1" />)}
                {getStatusLabel(client.status)}
              </span>
              {!client.is_active && (
                <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <Lock className="w-3 h-3 mr-1" />
                  Compte Désactivé
                </span>
              )}
            </div>
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center px-2.5 sm:px-4 py-1.5 sm:py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors shrink-0 ml-2"
            >
              <Edit className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Modifier</span>
            </button>
          </div>

          <div className="flex overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 border-t border-gray-200 dark:border-slate-800 scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center whitespace-nowrap px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {activeTab === 'company' && <CompanyTab client={client} onUpdate={(updated) => setClient(updated)} centralPurchasingName={centralPurchasingName} onReloadClientData={loadClientData} centralPurchasingId={client.central_purchasing_id} />}
        {activeTab === 'sales-points' && <SalesPointsTab salesPoints={salesPoints} clientId={Number(id)} onReload={loadSalesPoints} />}
        {activeTab === 'contacts' && <ContactsTab client={client} contacts={contacts} clientId={Number(id)} onReload={loadContacts} onReloadClient={loadClientData} />}
        {activeTab === 'admin' && <AdminTab client={client} onUpdate={(updated) => setClient(updated)} />}
        {activeTab === 'pricing' && <PricingTab pricing={pricing} client={client} onReload={loadPricing} onClientUpdate={loadClientData} centralPurchasingName={centralPurchasingName} centralPurchasingId={client.central_purchasing_id} />}
        {activeTab === 'orders' && (
          <OrdersTab
            orders={orders}
            clientId={Number(id)}
            onReload={loadOrders}
            isCreateOpen={orderModalOpen}
            onCreateOpenChange={setOrderModalOpen}
            pricing={pricing}
          />
        )}
        {activeTab === 'notes' && (
          <NotesTab
            notes={notes}
            newNote={newNoteContent}
            setNewNote={setNewNoteContent}
            onAddTextNote={handleAddTextNote}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            isTranscribing={isTranscribing}
            onDeleteNote={handleDeleteNote}
            onUpdateNote={handleUpdateNote}
            // Reminder props
            reminders={reminders}
            onCreateReminder={handleCreateReminder}
            onUpdateReminder={handleUpdateReminder}
            onDeleteReminder={handleDeleteReminder}
            onToggleReminderComplete={handleToggleReminderComplete}
            onSnoozeReminder={handleSnoozeReminder}
            currentUserRole={user?.role}
            currentUserId={user?.id}
            currentUser={user}
            client={client}
          />
        )}
      </div>

      <ClientEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        client={client}
        onSuccess={(updatedClient) => {
          setClient(updatedClient);
          showSuccess('Client mis à jour avec succès');
        }}
      />
    </div>
  );
};

export default ClientDetail;
